/* Tasks: kanban boards (floor + teams, via the Conductor).
   Board mutations are optimistic: the card moves now, the write follows, and a
   failure rolls back honestly. Tickets are room state, so other people's edits
   arrive through /sync like everything else on the floor. */
import { defineStore } from 'pinia'
import { generateKeyBetween } from 'fractional-indexing'
import {
  atriumApi,
  type BoardColumn, type BoardInfo, type Ticket, type TicketComment, type TicketDraft, type TicketEvent, type TicketStatus,
} from '@/lib/atriumApi'
import { sync } from '@/lib/sync'
import { useFloor } from '@/stores/floor'
import { useToasts } from '@/stores/toasts'

/** Reserved status for archived cards — the implicit "archive" list every board
 *  carries after its configurable columns. Kept in sync with the Conductor. */
export const ARCHIVED_STATUS = 'archived'

/** What a board starts with — the server holds the live set per board. */
export const DEFAULT_COLUMNS: BoardColumn[] = [
  { id: 'backlog', label: 'Backlog' },
  { id: 'todo', label: 'To do' },
  { id: 'doing', label: 'In progress' },
  { id: 'done', label: 'Done' },
]

/** Column accent palette, by position (columns are user-defined). */
export const COLUMN_COLORS = [
  'var(--p-away)', 'var(--p-cowork)', 'var(--gold)', 'var(--ok)',
  'var(--p-meeting)', 'var(--sage)', 'var(--p-break)', 'var(--live)',
]

export const PRIORITIES: Array<{ id: Ticket['priority']; label: string; color: string }> = [
  { id: 'low', label: 'Low', color: 'var(--p-away)' },
  { id: 'normal', label: 'Normal', color: 'var(--p-cowork)' },
  { id: 'high', label: 'High', color: 'var(--warn)' },
  { id: 'urgent', label: 'Urgent', color: 'var(--danger)' },
]

function byOrder(a: Ticket, b: Ticket): number {
  // Order is a fractional-index key (see internal/rank) compared as a string.
  // Cards the server hasn't keyed yet (legacy, pre-migration) fall back to age.
  const ao = a.order ?? ''
  const bo = b.order ?? ''
  if (ao !== bo) return ao < bo ? -1 : 1
  return (a.created_at ?? 0) - (b.created_at ?? 0)
}

interface TasksState {
  boards: BoardInfo[]
  boardsLoaded: boolean
  activeBoard: string
  columns: BoardColumn[]
  tickets: Ticket[]
  ticketsLoaded: boolean
  loading: boolean
  activity: TicketEvent[]
  activityLoaded: boolean
  activityLoading: boolean
  comments: TicketComment[]
  commentsLoaded: boolean
  inited: boolean
  refreshDebounce: ReturnType<typeof setTimeout> | null
}

export const useTasks = defineStore('tasks', {
  state: (): TasksState => ({
    boards: [],
    boardsLoaded: false,
    activeBoard: 'floor',
    columns: DEFAULT_COLUMNS,
    tickets: [],
    ticketsLoaded: false,
    loading: false,
    activity: [],
    activityLoaded: false,
    activityLoading: false,
    comments: [],
    commentsLoaded: false,
    inited: false,
    refreshDebounce: null,
  }),

  getters: {
    activeBoardInfo: (s): BoardInfo | undefined => s.boards.find((b) => b.id === s.activeBoard),
    /** A column's cards — plus, in the first column, any card whose status no
     *  longer names a column (so nothing silently disappears mid-reconfigure).
     *  Archived cards are never stranded into the first column: they live in the
     *  archive list, fetched by passing ARCHIVED_STATUS (newest-archived first). */
    column: (s) => (status: TicketStatus): Ticket[] => {
      const known = new Set(s.columns.map((c) => c.id))
      const here = s.tickets.filter(
        (t) =>
          t.status === status ||
          (!known.has(t.status) && t.status !== ARCHIVED_STATUS && status === s.columns[0]?.id),
      )
      return status === ARCHIVED_STATUS
        ? here.sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))
        : here.sort(byOrder)
    },
    /** The board's last column reads as "shipped" (struck-through cards). */
    doneColumnId: (s): string | undefined => s.columns[s.columns.length - 1]?.id,
    /** How many cards are tucked away in the archive. */
    archivedCount: (s): number => s.tickets.filter((t) => t.status === ARCHIVED_STATUS).length,
    commentsFor: (s) => (ticketId: string): TicketComment[] =>
      s.comments.filter((c) => c.ticket === ticketId).sort((a, b) => a.ts - b.ts),
    commentCount: (s) => (ticketId: string): number =>
      s.comments.reduce((n, c) => n + (c.ticket === ticketId ? 1 : 0), 0),
  },

  actions: {
    /** Subscribe once: ticket edits land as room state, so a state delta in
     *  /sync means our board may be stale → debounced reload. Comments and
     *  log entries are timeline events — those stream in directly. */
    init() {
      if (this.inited) return
      this.inited = true
      sync.on('state', () => {
        if (!this.ticketsLoaded) return
        if (this.refreshDebounce) clearTimeout(this.refreshDebounce)
        this.refreshDebounce = setTimeout(() => {
          void this.loadTickets(true)
          if (this.activityLoaded) void this.loadActivity(true)
        }, 600)
      })
      sync.on('custom', (roomId, ev) => {
        const id = ev.event_id
        if (!id) return
        const content = (ev.content || {}) as Record<string, any>
        if (this.boardIdOf(roomId, content.board || '') !== this.activeBoard) return
        const ts = content.ts || ev.origin_server_ts || Date.now()
        if (ev.type === 'app.atrium.ticket.comment' && content.ticket && content.body) {
          if (content.replaces) {
            // an edit (e.g. a task ticked off) — update the original in place
            const orig = this.comments.find((c) => c.id === content.replaces)
            if (orig) {
              orig.body = content.body
              orig.edited = true
            }
          } else if (!this.comments.some((c) => c.id === id)) {
            this.comments.unshift({ id, ticket: content.ticket, body: content.body, by: content.by, ts })
          }
        } else if (ev.type === 'app.atrium.ticket.event' && content.ticket && content.action) {
          if (this.activityLoaded && !this.activity.some((a) => a.id === id)) {
            this.activity.unshift({
              id, ticket: content.ticket, action: content.action, title: content.title,
              from: content.from, to: content.to, fields: content.fields,
              assignee: content.assignee, assignees: content.assignees,
              new_assignees: content.new_assignees, subscribers: content.subscribers,
              by: content.by, ts,
            })
          }
        }
      })
    },

    /** Map a Space room + board key back to a board id ("" key = the Space's
     *  default board: "floor" on the org Space, the team id on a team Space). */
    boardIdOf(roomId: string, key: string): string | null {
      const floor = useFloor()
      if (roomId === floor.orgSpace) return key || 'floor'
      const team = Object.values(floor.teams).find((t) => t.spaceId === roomId)
      if (team) return key || team.id
      return null
    },

    async loadBoards(): Promise<void> {
      try {
        const res = await atriumApi.boards()
        this.boards = res.boards
        if (!this.boards.some((b) => b.id === this.activeBoard)) this.activeBoard = 'floor'
        const active = this.boards.find((b) => b.id === this.activeBoard)
        if (active?.columns?.length) this.columns = active.columns
      } catch (e) {
        console.warn('[tasks] loading boards failed', e)
      } finally {
        this.boardsLoaded = true
      }
    },

    async setBoard(id: string): Promise<void> {
      if (id === this.activeBoard) return
      this.activeBoard = id
      this.tickets = []
      this.ticketsLoaded = false
      this.activity = []
      this.activityLoaded = false
      this.comments = []
      this.commentsLoaded = false
      const info = this.boards.find((b) => b.id === id)
      if (info?.columns?.length) this.columns = info.columns
      await this.loadTickets()
    },

    async loadTickets(quiet = false): Promise<void> {
      if (!quiet) this.loading = true
      const board = this.activeBoard
      try {
        const res = await atriumApi.tickets(board)
        if (board === this.activeBoard) {
          this.tickets = res.tickets
          if (res.columns?.length) this.columns = res.columns
        }
      } catch (e) {
        console.warn('[tasks] loading tickets failed', e)
      } finally {
        this.loading = false
        this.ticketsLoaded = true
      }
      if (!this.commentsLoaded) void this.loadComments(true)
    },

    /** The whole board's comments in one read — card counts + per-ticket
     *  threads come from the same list; new ones stream in via /sync. */
    async loadComments(quiet = false): Promise<void> {
      const board = this.activeBoard
      try {
        const res = await atriumApi.comments(board, { limit: 500 })
        if (board === this.activeBoard) this.comments = res.comments
      } catch (e) {
        if (!quiet) console.warn('[tasks] loading comments failed', e)
      } finally {
        this.commentsLoaded = true
      }
    },

    async addComment(ticketId: string, body: string): Promise<boolean> {
      try {
        const c = await atriumApi.addComment(this.activeBoard, ticketId, body)
        if (!this.comments.some((x) => x.id === c.id)) this.comments.unshift(c)
        return true
      } catch (e) {
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t post the comment',
          text: e instanceof Error ? e.message : undefined,
        })
        return false
      }
    },

    /** Edit a comment's body — in practice: ticking its task lines. Optimistic;
     *  the server enforces that non-authors only flip checkboxes. */
    async editComment(ticketId: string, eventId: string, body: string): Promise<void> {
      const c = this.comments.find((x) => x.id === eventId)
      if (!c) return
      const prev = c.body
      c.body = body
      c.edited = true
      try {
        await atriumApi.updateComment(this.activeBoard, ticketId, eventId, body)
      } catch (e) {
        c.body = prev
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t update the comment',
          text: e instanceof Error ? e.message : undefined,
        })
      }
    },

    async removeComment(ticketId: string, eventId: string): Promise<void> {
      const idx = this.comments.findIndex((c) => c.id === eventId)
      const removed = idx >= 0 ? this.comments.splice(idx, 1)[0] : null
      try {
        await atriumApi.deleteComment(this.activeBoard, ticketId, eventId)
      } catch (e) {
        if (removed) this.comments.splice(idx, 0, removed)
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t delete the comment',
          text: e instanceof Error ? e.message : undefined,
        })
      }
    },

    /** The board's activity log — newest first, straight off the Space's
     *  timeline (the Conductor writes an entry next to every change). */
    async loadActivity(quiet = false): Promise<void> {
      if (!quiet) this.activityLoading = true
      const board = this.activeBoard
      try {
        const res = await atriumApi.activity(board, { limit: 300 })
        if (board === this.activeBoard) this.activity = res.events
      } catch (e) {
        console.warn('[tasks] loading activity failed', e)
      } finally {
        this.activityLoading = false
        this.activityLoaded = true
      }
    },

    // ---- board management (extra boards + configurable columns) ----

    async createBoard(name: string, scope: string): Promise<BoardInfo | null> {
      try {
        const board = await atriumApi.createBoard(name, scope)
        await this.loadBoards()
        await this.setBoard(board.id)
        return board
      } catch (e) {
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t create the board',
          text: e instanceof Error ? e.message : undefined,
        })
        return null
      }
    },

    async updateBoard(id: string, patch: { name?: string; columns?: BoardColumn[] }): Promise<boolean> {
      try {
        const saved = await atriumApi.updateBoard(id, patch)
        const i = this.boards.findIndex((b) => b.id === id)
        if (i >= 0) this.boards[i] = { ...this.boards[i], name: saved.name, columns: saved.columns }
        if (id === this.activeBoard) {
          this.columns = saved.columns
          await this.loadTickets(true) // the server sweeps stranded cards
        }
        return true
      } catch (e) {
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t update the board',
          text: e instanceof Error ? e.message : undefined,
        })
        return false
      }
    },

    async deleteBoard(id: string): Promise<boolean> {
      try {
        await atriumApi.deleteBoard(id)
        this.boards = this.boards.filter((b) => b.id !== id)
        if (this.activeBoard === id) {
          this.activeBoard = 'floor'
          const info = this.boards.find((b) => b.id === 'floor')
          if (info?.columns?.length) this.columns = info.columns
          await this.loadTickets()
        }
        return true
      } catch (e) {
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t delete the board',
          text: e instanceof Error ? e.message : undefined,
        })
        return false
      }
    },

    async createTicket(draft: TicketDraft): Promise<Ticket | null> {
      try {
        const t = await atriumApi.createTicket(this.activeBoard, draft)
        this.tickets.push(t)
        return t
      } catch (e) {
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t create the ticket',
          text: e instanceof Error ? e.message : undefined,
        })
        return null
      }
    },

    async updateTicket(id: string, patch: TicketDraft): Promise<boolean> {
      const idx = this.tickets.findIndex((t) => t.id === id)
      if (idx < 0) return false
      const prev = { ...this.tickets[idx] }
      this.tickets[idx] = { ...prev, ...patch } as Ticket
      try {
        const saved = await atriumApi.updateTicket(this.activeBoard, id, patch)
        const i = this.tickets.findIndex((t) => t.id === id)
        if (i >= 0) this.tickets[i] = saved
        return true
      } catch (e) {
        const i = this.tickets.findIndex((t) => t.id === id)
        if (i >= 0) this.tickets[i] = prev
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t update the ticket',
          text: e instanceof Error ? e.message : undefined,
        })
        return false
      }
    },

    /** Follow (or mute) a card's activity — a private per-user preference. The
     *  server resubscribes/clears mute on follow, mutes on unfollow, and returns
     *  the card with the updated subscriber/muted lists. */
    async setTicketSubscription(id: string, follow: boolean): Promise<boolean> {
      try {
        const saved = await atriumApi.setSubscription(this.activeBoard, id, follow)
        const i = this.tickets.findIndex((t) => t.id === id)
        if (i >= 0) this.tickets[i] = saved
        return true
      } catch (e) {
        useToasts().push({
          icon: 'x', color: 'var(--danger)',
          title: follow ? 'Couldn’t follow this task' : 'Couldn’t mute this task',
          text: e instanceof Error ? e.message : undefined,
        })
        return false
      }
    },

    /** Move a card into a column, in front of `before` (null = the bottom).
     *  Order is a fractional index: a key strictly between the two neighbours
     *  always exists, so the drop is a single write — no matter the slot, and
     *  with no column-wide renumber to trip the homeserver's rate limit.
     *  Identifying the slot by card (not index) keeps the drop honest when
     *  the rendered list is filtered ("Mine") or holds stranded cards. */
    async moveTicket(id: string, status: TicketStatus, before: string | null): Promise<void> {
      const neighbors = this.column(status).filter((t) => t.id !== id)
      let at = before ? neighbors.findIndex((t) => t.id === before) : -1
      if (at < 0) at = neighbors.length
      const prev = neighbors[at - 1]?.order || null
      const next = neighbors[at]?.order || null
      // Backfill happens server-side on board load, so neighbours carry valid
      // keys by the time a card can be dragged. If a stale/legacy key slips
      // through, generateKeyBetween throws rather than corrupt the order — fall
      // back to the bottom of the column so the drop still lands.
      let order: string
      try {
        order = generateKeyBetween(prev, next)
      } catch {
        const tail = neighbors[neighbors.length - 1]?.order || null
        order = generateKeyBetween(tail, null)
      }
      await this.updateTicket(id, { status, order })
    },

    /** Send a card to another board — it leaves this one. The server keeps
     *  its column when the destination has one by the same id, carries the
     *  conversation along, and logs the transfer on both boards. */
    async moveTicketToBoard(id: string, to: string): Promise<boolean> {
      if (to === this.activeBoard) return false
      const idx = this.tickets.findIndex((t) => t.id === id)
      if (idx < 0) return false
      const removed = this.tickets.splice(idx, 1)[0]
      try {
        await atriumApi.moveTicketToBoard(this.activeBoard, id, to)
        const dest = this.boards.find((b) => b.id === to)
        useToasts().push({
          icon: 'kanban', color: 'var(--gold)', title: `Moved to ${dest?.name ?? to}`,
          text: removed.title,
        })
        return true
      } catch (e) {
        this.tickets.splice(idx, 0, removed)
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t move the ticket',
          text: e instanceof Error ? e.message : undefined,
        })
        return false
      }
    },

    /** Send a card to the archive (declutters "done"). Drag-drop routes through
     *  moveTicket; this is the button path (e.g. the ticket editor). */
    async archiveTicket(id: string): Promise<void> {
      await this.moveTicket(id, ARCHIVED_STATUS, null)
    },

    /** Pull a card back out of the archive into a live column. */
    async restoreTicket(id: string, status?: TicketStatus): Promise<void> {
      await this.moveTicket(id, status || this.columns[0]?.id || '', null)
    },

    async removeTicket(id: string): Promise<void> {
      const idx = this.tickets.findIndex((t) => t.id === id)
      if (idx < 0) return
      const removed = this.tickets[idx]
      this.tickets.splice(idx, 1)
      try {
        await atriumApi.deleteTicket(this.activeBoard, id)
      } catch (e) {
        this.tickets.splice(idx, 0, removed)
        useToasts().push({
          icon: 'x', color: 'var(--danger)', title: 'Couldn’t delete the ticket',
          text: e instanceof Error ? e.message : undefined,
        })
      }
    },
  },
})
